import { useEffect } from "react";
import { useEditor } from "@tldraw/tldraw";
import { SCALE_CURSOR_BY_TYPE } from "../new-tldraw-components/scaleResizeCursors";

/**
 * Hook that manages cursor behavior during resize and rotate operations
 * Ensures proper cursors (resize: ew-resize, ns-resize, nwse-resize, nesw-resize; rotate: grab, grabbing)
 * are displayed during shape manipulation
 */
export const useResizeCursor = () => {
  const editor = useEditor();

  useEffect(() => {
    let previousPath = "";

    const unsubscribe = editor.store.listen(() => {
      try {
        const path = editor.getPath();
        const canvas = document.querySelector(".tl-canvas") as HTMLElement;

        if (!canvas) {
          return;
        }

        // Check if we're in a resize, rotate, or crop state
        const isResizing =
          path.includes("resizing") ||
          path.includes("pointing_resize_handle") ||
          path.includes("cropping") ||
          path.includes("pointing_crop_handle");

        const wasResizing =
          previousPath.includes("resizing") ||
          previousPath.includes("pointing_resize_handle") ||
          previousPath.includes("cropping") ||
          previousPath.includes("pointing_crop_handle");

        const isRotating = path.includes("rotating") || path.includes("pointing_rotate_handle");
        const wasRotating = previousPath.includes("rotating") || previousPath.includes("pointing_rotate_handle");

        if (isResizing) {
          // Get the current cursor type from tldraw's editor
          const editorCursor = editor.getInstanceState().cursor;

          // Map tldraw cursor types to CSS cursor values
          let cursorValue = "default";
          if (editorCursor?.type) {
            const cursorType = editorCursor.type;
            const rotation = editorCursor.rotation || 0;

            // Map tldraw's cursor types to CSS cursors
            const cursorMap: Record<string, string> = {
              "nwse-resize": "nwse-resize",
              "nesw-resize": "nesw-resize",
              "ew-resize": "ew-resize",
              "ns-resize": "ns-resize",
            };

            cursorValue = cursorMap[cursorType] || cursorType;

            // While Scale mode is on, swap the active-drag cursor to
            // the custom scale-arrow glyph for the matching
            // direction. The `useScaleResizeCursor` hook handles the
            // hover-on-handle case via `--tl-cursor`; this inline
            // override is needed only during the drag itself, because
            // this hook writes `cursor: <type>` directly on the
            // canvas while resizing — which would otherwise beat the
            // CSS-var path.
            const isScaleModeActive =
              typeof document !== "undefined" && document.body.classList.contains("is-scale-mode-active");
            if (isScaleModeActive && SCALE_CURSOR_BY_TYPE[cursorType]) {
              cursorValue = SCALE_CURSOR_BY_TYPE[cursorType];
            }

            // Apply rotation to the canvas container to rotate the cursor
            // Convert radians to degrees for CSS transform
            const rotationDegrees = (rotation * 180) / Math.PI;
            canvas.style.setProperty("--cursor-rotation", `${rotationDegrees}deg`);
          }

          canvas.style.setProperty("cursor", cursorValue, "important");
        } else if (isRotating) {
          // Get the current cursor type from tldraw's editor
          const editorCursor = editor.getInstanceState().cursor;

          if (editorCursor?.type) {
            const cursorType = editorCursor.type;

            // For rotate, add a CSS class instead of inline style
            // This allows the CSS rules to handle the cursor (cursor: grab !important)
            const cursorClass = `tl-cursor-${cursorType}`;

            // Remove any previous tl-cursor-* classes
            canvas.className = canvas.className
              .split(" ")
              .filter((c) => !c.startsWith("tl-cursor-"))
              .join(" ");

            // Add the new cursor class
            canvas.classList.add(cursorClass);
          }
        } else if ((wasResizing && !isResizing) || (wasRotating && !isRotating)) {
          // Just finished resizing or rotating - let tldraw handle the cursor

          // Remove cursor classes for rotate
          canvas.className = canvas.className
            .split(" ")
            .filter((c) => !c.startsWith("tl-cursor-"))
            .join(" ");

          canvas.style.removeProperty("cursor");

          // After a short delay, check if we should restore custom cursor
          // This allows tldraw to set its cursor first
          setTimeout(() => {
            const currentPath = editor.getPath();
            const editorCursor = editor.getInstanceState().cursor;

            // If in idle state with default cursor, restore the appropriate cursor
            if (currentPath === "select.idle" && editorCursor?.type === "default") {
              // Check if scale mode is active (any selected shape has isScale: true)
              const selectedIds = editor.getSelectedShapeIds();
              const isScaleActive = selectedIds.some((id) => {
                const shape = editor.getShape(id);
                return shape && (shape.props as any)?.isScale;
              });

              if (isScaleActive) {
                // Scale mode uses the same cursor as select — leave
                // the property cleared so tldraw's default flows
                // through (was previously overriding with scaleCursor).
              } else {
                // Restore generic custom cursor
                const customCursor = `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAWCAYAAADJqhx8AAAACXBIWXMAAAsSAAALEgHS3X78AAABVklEQVQ4jWP8//8/AyWA6f+v3z/+3rhnS64ZTP+/fPvPKCq0h1xDmP6//fDjS34rGwMDw7a/j5/7kGwAiPj34g3Dl4peHkYGxlX/3n3MItkAEPj/5RvD59xmToav33tIMYQJmYNsyN8HT0tINgDFkP//O/5cvlVHsgEwQ74UtjMz8nDVETIEqwHIhjAwMtbiMwSnAXBDCtpY/r16W/3vxZulJBsAA9/aZ7H9uXo75N/bD5vIMoABZsiF6+7ohhBtALIhf+8+PgITY8GlmEVfA6v4r22H2DizoqxBhjAry9pgNYCrMu0Xi7bqhf+fvlzHasrfvwwMjIyvsboApJlZTWE+k4RIBoOECEFvwQ1g5OFi4G4p+MPw/38rs6xkE0GdUAAORJBmnv7Kvwz//zez6KoRrRninScvPv+59/jP70s360DFG6kYRPwnVzPMAANyNf///58BANgjLIKd0D0WAAAAAElFTkSuQmCC") 2 0, auto`;
                canvas.style.setProperty("cursor", customCursor, "important");
              }
            }
          }, 50);
        }

        // Update previousPath AFTER checking wasResizing
        previousPath = path;
      } catch (error) {
        console.error("useResizeCursor error:", error);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [editor]);
};

/** Renders nothing; manages cursor state during resize and rotate operations */
export function ResizeCursorManager() {
  useResizeCursor();
  return null;
}
